import React, { useState } from 'react';
import {
  AudioLines, Bell, CalendarCheck, FileText, Mail, Megaphone, MessageSquareText, Mic, Monitor, PhoneMissed, Settings2, Users, UserPlus,
} from 'lucide-react';
import { apiError } from '../../../../lib/host';
import { useT } from '../../../../lib/i18n';
import { usePreferences } from '../../preferences/PreferencesContext';
import { ErrorBanner, useBasic } from '../../ui';
import { Card, SectionHead, Toggle, ToggleRow } from '../components/SettingsUI';

const CHANNELS = [
  ['email', Mail, 'blue', 'Email Notifications', 'Receive alerts via email'],
  ['in_app', Bell, 'red', 'In-App Notifications', 'Show alerts in the dashboard'],
  ['sms', MessageSquareText, 'purple', 'SMS Notifications', 'Receive important alerts via SMS'],
];
const TYPES = [
  ['missed_calls', PhoneMissed, 'red', 'Missed Calls', 'Get notified when you miss a call'],
  ['new_leads', UserPlus, 'blue', 'New Leads', 'Get notified when a new lead is captured'],
  ['appointments', CalendarCheck, 'orange', 'Appointments', 'Get notified for new and upcoming appointments'],
  ['voicemails', AudioLines, 'purple', 'Voicemails', 'Get notified when a new voicemail is received'],
  ['call_recordings', Mic, 'teal', 'Call Recordings', 'Get notified when call recordings are available'],
  ['team_activity', Users, 'blue', 'Team Activity', 'Get notified about team member activities'],
  ['system_billing', Settings2, 'slate', 'System & Billing Alerts', 'Get important updates about your plan, usage and system status'],
];
const HOURS = Array.from({ length: 48 }, (_, i) => `${String(Math.floor(i / 2)).padStart(2, '0')}:${i % 2 ? '30' : '00'}`);
const label12 = (hhmm) => {
  const [h, m] = hhmm.split(':').map(Number);
  return `${String(((h + 11) % 12) + 1).padStart(2, '0')}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
};

function HoursPicker({ value, onChange, disabled, t }) {
  return (
    <div className={`st-hours ${disabled ? 'off' : ''}`}>
      <select value={value.start} disabled={disabled} aria-label={t('From')} onChange={(e) => onChange({ ...value, start: e.target.value })}>
        {HOURS.map((h) => <option key={h} value={h}>{label12(h)}</option>)}
      </select>
      <span>{t('to')}</span>
      <select value={value.end} disabled={disabled} aria-label={t('To')} onChange={(e) => onChange({ ...value, end: e.target.value })}>
        {HOURS.map((h) => <option key={h} value={h}>{label12(h)}</option>)}
      </select>
    </div>
  );
}

/** Settings › Notifications: channels, which events by which channel, working / quiet hours, and extras. */
export default function NotificationsSection() {
  const { t } = useT();
  const { notify } = useBasic();
  const { prefs, save } = usePreferences();
  const [form, setForm] = useState(prefs.notifications);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const dirty = JSON.stringify(form) !== JSON.stringify(prefs.notifications);

  const setChannel = (c, v) => setForm({ ...form, channels: { ...form.channels, [c]: v } });
  const setType = (type, c, v) => setForm({ ...form, types: { ...form.types, [type]: { ...form.types[type], [c]: v } } });
  const setColumn = (c, v) => setForm({ ...form, types: Object.fromEntries(Object.entries(form.types).map(([k, x]) => [k, { ...x, [c]: v }])) });
  const allOn = (c) => TYPES.every(([k]) => form.types[k][c]);

  const setDesktop = async (v) => {
    if (v && 'Notification' in window && Notification.permission === 'default') {
      try { await Notification.requestPermission(); } catch { /* the browser decides */ }
    }
    if (v && 'Notification' in window && Notification.permission === 'denied') {
      setError(t('Your browser is blocking notifications for this site. Allow them in the browser settings first.'));
    }
    setForm((f) => ({ ...f, desktop: v }));
  };

  const submit = async () => {
    setBusy(true);
    setError('');
    try {
      await save('notifications', form);
      notify(t('Notification preferences saved.'));
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <SectionHead title={t('Notifications')} subtitle={t('Control how and when you receive alerts about your calls, leads, appointments and account.')}>
        <button className="primary-action" disabled={busy || !dirty} onClick={submit}>{busy ? t('Saving…') : t('Save Changes')}</button>
      </SectionHead>
      <ErrorBanner message={error} />

      <Card title={t('Notification Channels')} subtitle={t('Choose how you want to receive notifications.')}>
        <div className="st-channel-cards">
          {CHANNELS.map(([key, Icon, tone, title, sub]) => (
            <div key={key} className="st-channel">
              <span className={`st-icon ${tone}`}><Icon size={18} /></span>
              <span className="st-toggle-copy"><b>{t(title)}</b><small>{t(sub)}</small></span>
              <Toggle checked={form.channels[key]} onChange={(v) => setChannel(key, v)} label={t(title)} />
            </div>
          ))}
        </div>
      </Card>

      <Card title={t('Notification Types')} subtitle={t('Select the events you want to be notified about.')}>
        <div className="table-scroll">
          <table className="st-matrix">
            <thead>
              <tr><th aria-label={t('Event')} /><th aria-label={t('Description')} />
                {CHANNELS.map(([c, , , title]) => (
                  <th key={c}><label className="st-check-head"><input type="checkbox" checked={allOn(c)} disabled={!form.channels[c]}
                    onChange={(e) => setColumn(c, e.target.checked)} aria-label={t('All by {channel}', { channel: t(title) })} />{t(c === 'in_app' ? 'In-App' : c === 'sms' ? 'SMS' : 'Email')}</label></th>
                ))}
              </tr>
            </thead>
            <tbody>
              {TYPES.map(([key, Icon, tone, title, sub]) => (
                <tr key={key}>
                  <td><span className="st-type"><Icon size={17} className={`st-tone-${tone}`} /><b>{t(title)}</b></span></td>
                  <td className="st-type-sub">{t(sub)}</td>
                  {CHANNELS.map(([c]) => (
                    <td key={c}><input type="checkbox" checked={!!form.types[key][c]} disabled={!form.channels[c]}
                      aria-label={`${t(title)}: ${c}`} onChange={(e) => setType(key, c, e.target.checked)} /></td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {form.channels.sms && <small className="st-hint st-pad">{t('SMS goes to the phone number in your profile.')}</small>}
      </Card>

      <div className="st-two">
        <Card title={t('Notification Timing')} subtitle={t('Set working hours and quiet hours for notifications.')}>
          <div className="st-timing">
            <div className="st-inline-toggle">
              <Toggle checked={form.working_hours.enabled} onChange={(v) => setForm({ ...form, working_hours: { ...form.working_hours, enabled: v } })} label={t('Working Hours')} />
              <span><b>{t('Working Hours')}</b><small>{t('Receive non-urgent notifications only during working hours.')}</small></span>
            </div>
            <HoursPicker t={t} value={form.working_hours} disabled={!form.working_hours.enabled} onChange={(v) => setForm({ ...form, working_hours: v })} />
            <div className="st-inline-toggle">
              <Toggle checked={form.quiet_hours.enabled} onChange={(v) => setForm({ ...form, quiet_hours: { ...form.quiet_hours, enabled: v } })} label={t('Quiet Hours')} />
              <span><b>{t('Quiet Hours')}</b><small>{t('Pause non-urgent notifications during quiet hours.')}</small></span>
            </div>
            <HoursPicker t={t} value={form.quiet_hours} disabled={!form.quiet_hours.enabled} onChange={(v) => setForm({ ...form, quiet_hours: v })} />
            <small className="st-hint">{t('Applies to email and SMS. System & billing alerts always arrive; the bell keeps everything.')}</small>
          </div>
        </Card>
        <Card title={t('Additional Options')} subtitle={t('Customize your notification experience.')}>
          <ToggleRow icon={Bell} tone="blue" title={t('Sound Alerts')} sub={t('Play a sound for in-app notifications')} checked={form.sound} onChange={(v) => setForm({ ...form, sound: v })} />
          <ToggleRow icon={Monitor} tone="blue" title={t('Desktop Notifications')} sub={t('Show browser notifications (when allowed)')} checked={form.desktop} onChange={setDesktop} />
          <ToggleRow icon={FileText} tone="blue" title={t('Notification Summary')} sub={t('Receive a daily summary email')} checked={form.daily_summary} onChange={(v) => setForm({ ...form, daily_summary: v })} />
          <ToggleRow icon={Megaphone} tone="red" title={t('Marketing Updates')} sub={t('Receive product updates and tips')} checked={form.marketing} onChange={(v) => setForm({ ...form, marketing: v })} />
        </Card>
      </div>
    </>
  );
}
